import type { ProjectSettings } from '../types';

const URL_LIST_KEYS: Array<keyof ProjectSettings> = [
    'entryUrls',
    'innerUrls',
    'exitUrls',
    'referralUrls',
];

const rawValues = (value: unknown): string[] => {
    if (value === undefined || value === null) return [];
    if (typeof value === 'string') return [value];
    if (Array.isArray(value)) return value.flatMap(rawValues);
    if (typeof value === 'object') {
        const maybeUrl = (value as { url?: unknown; href?: unknown }).url ?? (value as { href?: unknown }).href;
        return maybeUrl ? [String(maybeUrl)] : [];
    }
    return [String(value)];
};

export type UrlListIssueLevel = 'warning' | 'error';

export interface UrlListIssue {
    level: UrlListIssueLevel;
    line?: number;
    value?: string;
    message: string;
}

export interface UrlListAnalysis {
    urls: string[];
    cleanedValue: string;
    validCount: number;
    invalidCount: number;
    duplicateCount: number;
    autoCleanedCount: number;
    hasErrors: boolean;
    hasWarnings: boolean;
    issues: UrlListIssue[];
}

const parseUrlCandidates = (value: unknown): string[] => {
    const urls: string[] = [];

    rawValues(value).forEach((raw) => {
        const normalized = raw
            .replace(/\r\n?/g, '\n')
            .replace(/(^|[^?&=])(https?:\/\/)/gi, '$1\n$2')
            .trim();

        normalized
            .split(/[\s,]+/)
            .map((part) => part.trim().replace(/^["'<>]+|["'<>]+$/g, '').replace(/,+$/g, ''))
            .filter(Boolean)
            .forEach((url) => urls.push(url));
    });

    return urls;
};

export const parseUrlList = (value: unknown): string[] => {
    return Array.from(new Set(parseUrlCandidates(value)));
};

export const normalizeUrlList = (value: unknown): string => parseUrlList(value).join('\n');

const isHttpUrl = (value: string): boolean => {
    try {
        const parsed = new URL(value);
        return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch {
        return false;
    }
};

export const analyzeUrlList = (value: unknown, options: { required?: boolean } = {}): UrlListAnalysis => {
    const allCandidates = parseUrlCandidates(value);
    const urls = parseUrlList(value);
    const issues: UrlListIssue[] = [];
    const seen = new Set<string>();
    const duplicates = new Set<string>();

    rawValues(value).forEach((rawValue) => {
        rawValue.replace(/\r\n?/g, '\n').split('\n').forEach((line, index) => {
            const trimmed = line.trim();
            if (!trimmed) return;

            const lineCandidates = parseUrlCandidates(trimmed);
            const protocolCount = (trimmed.match(/https?:\/\//gi) || []).length;

            if (trimmed.includes(',')) {
                issues.push({
                    level: 'warning',
                    line: index + 1,
                    message: 'Contains commas and will be split automatically.',
                });
            }

            if (lineCandidates.length > 1 || protocolCount > 1) {
                issues.push({
                    level: 'warning',
                    line: index + 1,
                    message: 'Contains multiple URLs and will be split automatically.',
                });
            }

            lineCandidates.forEach((candidate) => {
                if (!isHttpUrl(candidate)) {
                    issues.push({
                        level: 'error',
                        line: index + 1,
                        value: candidate,
                        message: 'Use a complete http:// or https:// URL.',
                    });
                }
            });
        });
    });

    allCandidates.forEach((candidate) => {
        if (seen.has(candidate)) {
            duplicates.add(candidate);
        }
        seen.add(candidate);
    });

    duplicates.forEach((duplicate) => {
        issues.push({
            level: 'warning',
            value: duplicate,
            message: 'Duplicate URL will be saved once.',
        });
    });

    const invalidCount = allCandidates.filter((candidate) => !isHttpUrl(candidate)).length;

    if (options.required && urls.length === 0) {
        issues.push({
            level: 'error',
            message: 'At least one URL is required.',
        });
    }

    const originalText = rawValues(value).join('\n').replace(/\r\n?/g, '\n').trim();
    const cleanedValue = urls.join('\n');
    const structureChanged = originalText.length > 0 && originalText !== cleanedValue;
    const autoCleanedCount = [
        structureChanged,
        duplicates.size > 0,
        issues.some((issue) => issue.level === 'warning' && issue.message.includes('automatically')),
    ].filter(Boolean).length;

    return {
        urls,
        cleanedValue,
        validCount: urls.filter(isHttpUrl).length,
        invalidCount,
        duplicateCount: duplicates.size,
        autoCleanedCount,
        hasErrors: issues.some((issue) => issue.level === 'error'),
        hasWarnings: issues.some((issue) => issue.level === 'warning'),
        issues,
    };
};

export const normalizeProjectUrlSettings = <T extends Partial<ProjectSettings> | undefined>(settings: T): T => {
    if (!settings) return settings;
    const normalized: Partial<ProjectSettings> = { ...settings };
    URL_LIST_KEYS.forEach((key) => {
        if (key in normalized) {
            (normalized as Record<string, unknown>)[key] = normalizeUrlList((normalized as Record<string, unknown>)[key]);
        }
    });
    return normalized as T;
};
